import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  ControlledSelectField,
  SelectOption,
} from 'src/common/components/form/controlled/ControlledSelectField';
import { snapOverlayToActivator } from 'src/helpers/dndModifiers';
import { IFormEdition } from 'src/interfaces/Edition';

type CompetitionField = { id: string };

function optionLabel(options: SelectOption[], value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return options.find(o => o.value === value)?.label ?? value;
}

function CompetitionDragPreview({
  competitionLabel,
  distancesLabel,
}: {
  competitionLabel: string;
  distancesLabel: string;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        boxShadow: 4,
        cursor: 'grabbing',
        bgcolor: 'background.paper',
        minWidth: 280,
      }}
    >
      <DragIndicatorIcon fontSize="small" color="action" />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" noWrap>
          {competitionLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {distancesLabel}
        </Typography>
      </Box>
    </Paper>
  );
}

interface SortableCompetitionRowProps {
  field: CompetitionField;
  index: number;
  control: Control<IFormEdition>;
  systemOptions: SelectOption[];
  distanceOptions: SelectOption[];
  onRemove: (index: number) => void;
}

function SortableCompetitionRow({
  field,
  index,
  control,
  systemOptions,
  distanceOptions,
  onRemove,
}: SortableCompetitionRowProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  return (
    <Grid
      size={{ xs: 12 }}
      ref={setNodeRef}
      sx={{
        transform: isDragging ? undefined : CSS.Transform.toString(transform),
        transition: isDragging ? undefined : transition,
        opacity: isDragging ? 0.35 : 1,
      }}
    >
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid size="auto">
          <Tooltip title={t('editions.dragCompetition')}>
            <IconButton
              size="small"
              {...attributes}
              {...listeners}
              aria-label={t('editions.dragCompetition')}
              sx={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid size="grow">
          <ControlledSelectField<IFormEdition>
            name={`competitions.${index}.competitionId`}
            control={control}
            fullWidth
            label={t('editions.competitionLabel')}
            margin="dense"
            options={systemOptions}
            withEmptyOption
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ControlledSelectField<IFormEdition>
            name={`competitions.${index}.distances`}
            control={control}
            fullWidth
            label={t('editions.distancesLabel')}
            margin="dense"
            options={distanceOptions}
            withEmptyOption
            multiple
          />
        </Grid>
        <Grid size="auto">
          <Tooltip title={t('editions.removeCompetition')}>
            <IconButton
              onClick={() => onRemove(index)}
              color="error"
              size="small"
              aria-label={t('editions.removeCompetition')}
            >
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
  );
}

interface SortableEditionCompetitionsProps {
  fields: CompetitionField[];
  control: Control<IFormEdition>;
  systemOptions: SelectOption[];
  distanceOptions: SelectOption[];
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}

export default function SortableEditionCompetitions({
  fields,
  control,
  systemOptions,
  distanceOptions,
  onMove,
  onRemove,
}: SortableEditionCompetitionsProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const competitions = useWatch({ control, name: 'competitions' }) ?? [];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onMove(oldIndex, newIndex);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  if (fields.length === 0) {
    return null;
  }

  const activeIndex = activeId ? fields.findIndex(f => f.id === activeId) : -1;
  const activeCompetition = activeIndex >= 0 ? competitions[activeIndex] : undefined;
  const previewCompetitionLabel = optionLabel(systemOptions, activeCompetition?.competitionId);
  const previewDistancesLabel = (activeCompetition?.distances ?? [])
    .map(id => optionLabel(distanceOptions, id))
    .join(', ');

  return (
    <Box sx={{ width: '100%' }}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
          <Grid container spacing={2}>
            {fields.map((field, index) => (
              <SortableCompetitionRow
                key={field.id}
                field={field}
                index={index}
                control={control}
                systemOptions={systemOptions}
                distanceOptions={distanceOptions}
                onRemove={onRemove}
              />
            ))}
          </Grid>
        </SortableContext>
        <DragOverlay modifiers={[snapOverlayToActivator]} dropAnimation={null}>
          {activeId ? (
            <CompetitionDragPreview
              competitionLabel={previewCompetitionLabel}
              distancesLabel={previewDistancesLabel || '—'}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </Box>
  );
}
